import { ButtonDropdown, ButtonV2, DataGridRef, FilterV2, SearchV2, SortV2, StandardTabsV2 } from '@cimplico/components';
import { useRef, useState } from 'react';
import { useIsFirmRole } from '../hooks/use-is-firm-role';
import { Group } from '../types/group';
import PageContainer from '../components/page-container/PageContainer';
import PageTitle from '../components/PageTitle';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import GroupsDeleteConfirm from '../features/groups/GroupsDeleteConfirm';
import GroupsTable from '../features/groups/GroupsTable';
import GrowHeightContainer from '../components/GrowHeightContainer';
import { useNavigate } from 'react-router-dom';
import { useFirmContext } from '../providers/FirmProvider';
import { useIsActiveSubscription } from '../hooks/use-is-active-subscription';
import { useExportGroups } from '../queries/use-export-groups';
import { ArrowDownTrayIcon } from '@heroicons/react/20/solid';
import { useIntercom } from 'react-use-intercom';
import { toastNotification } from '../components/toast/Toast';
import { intercomEvents } from '../intercom-events';

const sortOptions = [
    { value: 'name', label: 'Name' },
    { value: 'updatedAt', label: 'Last Updated' },
    { value: 'entities', label: 'Entities' },
    { value: 'activeWorkpapers', label: 'Open Workpapers' },
];

function GroupsPage(): JSX.Element {
    const { trackEvent } = useIntercom();
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('');
    const tableRef = useRef<DataGridRef | null>(null);
    const { isExternalUser } = useIsFirmRole();

    const [filter, setFilter] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);

    const navigate = useNavigate();
    const { integrations } = useFirmContext();
    const activeSubscription = useIsActiveSubscription();

    const fyiIntegration = Boolean(integrations.fyi?.cabinet);

    function redirectToNewGroup(provider?: string): void {
        if (provider) {
            navigate(`new?provider=${provider}`);
        } else {
            navigate('new');
        }
    }
    function resetTable(): void {
        setIsOpen(false);
        setSelectedGroups([]);
        tableRef?.current?.clear();
        tableRef?.current?.resetPagination();
    }

    const downloadQuery = useExportGroups({ search, sort, filter });

    function download(): void {
        downloadQuery.mutate(undefined, {
            onSuccess: () => {
                // Send to intercom
                trackEvent(intercomEvents.groups.exportDataToCsv.event);
            },
            onError: (error) => {
                toastNotification('error', `${error.message}`);
            },
        });
    }

    return (
        <PageContainer>
            <GroupsDeleteConfirm groups={selectedGroups} isOpen={isOpen} onClose={(): void => setIsOpen(false)} onDelete={resetTable} />

            <div className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <PageTitle title="Groups" />
                    {selectedGroups.length > 0 && (
                        <SecondaryPageActions>
                            <SecondaryPageActions.Button
                                type="danger"
                                label={`Delete (${selectedGroups.length})`}
                                disabled={isExternalUser}
                                onClick={(): void => setIsOpen(true)}
                            />
                        </SecondaryPageActions>
                    )}
                    {selectedGroups.length === 0 && (
                        <div className="flex gap-4 items-center">
                            <ButtonDropdown
                                variant="primary"
                                label="New Group"
                                onClick={redirectToNewGroup}
                                size="sm"
                                disabled={!activeSubscription || isExternalUser}
                            >
                                {fyiIntegration && (
                                    <ButtonDropdown.SecondaryAction
                                        label="Add from FYI"
                                        onClick={(): void => redirectToNewGroup('fyi')}
                                        disabled={!activeSubscription || isExternalUser}
                                    />
                                )}
                            </ButtonDropdown>
                        </div>
                    )}
                </div>

                <section>
                    <div className="border-b">
                        <div className="inline-flex gap-4 h-11" />
                    </div>
                    <GrowHeightContainer className="flex flex-wrap items-center gap-2 mt-4 w-full">
                        <FilterV2
                            onChange={({ queryString }): void => {
                                setFilter(queryString);
                            }}
                        >
                            <FilterV2.NumericFilter id="entities" label="Entities" />
                            <FilterV2.DateFilter id="updatedAt" label="Last Updated" />
                            <FilterV2.NumericFilter id="activeWorkpapers" label="Open Workpapers" />
                        </FilterV2>
                        <SearchV2 onChange={setSearch} placeholder="Search..." />
                        <SortV2
                            options={sortOptions}
                            defaultValue="name"
                            defaultDirection="asc"
                            onChange={({ queryString }): void => {
                                setSort(queryString);
                            }}
                        />
                        <div className="ml-auto">
                            <ButtonV2
                                leftIcon={<ArrowDownTrayIcon />}
                                type="secondary"
                                size="sm"
                                label="Export"
                                onClick={download}
                                isDisabled={downloadQuery.isLoading}
                            />
                        </div>
                    </GrowHeightContainer>
                </section>
            </div>
            <section className="mt-4 mx-auto">
                <GroupsTable search={search} sort={sort} filter={filter} onSelect={setSelectedGroups} />
            </section>
        </PageContainer>
    );
}
export default GroupsPage;
